// WPS-005 + WPS-006 · Templates Gallery
// Visual admin browser of seeded category-aware templates (Bold + Minimal × 22 cat).
// Each card renders via CardCanvas with the template's colors + sample data.
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CardCanvas from '../CardCanvas';
import client from '../../services/api';
import { Search, Filter } from 'lucide-react';

// Use the public API methods directly
const fetchAll = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  const [cats, tpls] = await Promise.all([
    fetch(`${base}/categories`).then(r => r.json()),
    fetch(`${base}/templates`).then(r => r.json()),
  ]);
  return { cats: cats || [], tpls: tpls || [] };
};

const TemplatesGallery = () => {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [activeStyle, setActiveStyle] = useState('all');     // all | bold | minimal
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { cats, tpls } = await fetchAll();
      setCategories(cats);
      setTemplates(tpls);
      setLoading(false);
    })();
  }, []);

  const filtered = templates.filter(t => {
    if (activeStyle !== 'all' && t.style !== activeStyle) return false;
    if (activeCategory !== 'all' && t.categorySlug !== activeCategory) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!t.name?.toLowerCase().includes(q) && !t.categorySlug?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by category
  const byCategory = {};
  filtered.forEach(t => {
    const k = t.categorySlug || 'uncategorized';
    if (!byCategory[k]) byCategory[k] = [];
    byCategory[k].push(t);
  });

  const sampleCard = (template) => ({
    companyName: 'Acme Services',
    personalName: 'Carlos Herrera',
    jobTitle: 'Owner',
    email: 'carlos@acme.com',
    phone: '+1 214 949 7999',
    website: 'acme.com',
    address: 'Dallas, TX',
    slogan: template.defaultSlogan || '',
    colors: template.colors || ['#0a0a0a', '#c9a961'],
    qrPosition: 'back',
    orientation: 'horizontal',
    backDesign: { enabled: true, layout: 'minimal-text+qr', showQR: true },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Templates Gallery</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              {templates.length} category-aware starter templates · Bold + Minimal × {categories.length} categories
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates…"
                className="pl-8 w-56"
              />
            </div>
            <select
              value={activeStyle}
              onChange={(e) => setActiveStyle(e.target.value)}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All styles</option>
              <option value="bold">Bold only</option>
              <option value="minimal">Minimal only</option>
            </select>
            <select
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All categories</option>
              {categories.map(c => (
                <option key={c.slug} value={c.slug}>{c.icon || ''} {c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-12 text-gray-500 font-mono text-sm">Loading templates…</div>}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Filter className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No templates match the current filters</p>
          </div>
        )}

        {!loading && Object.entries(byCategory).map(([slug, tpls]) => {
          const cat = categories.find(c => c.slug === slug);
          return (
            <div key={slug} className="mb-8">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                <span className="text-xl">{cat?.icon || '·'}</span>
                <h3 className="text-lg font-semibold">{cat?.name || slug}</h3>
                <span className="text-xs font-mono text-gray-500 uppercase tracking-wider ml-2">
                  {tpls.length} template{tpls.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {tpls.map(t => (
                  <div key={t.id} className="border border-gray-200 p-4 hover:border-blue-400 hover:shadow-sm transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold text-sm">{t.name}</div>
                        <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mt-1">
                          {t.style} · {(t.colors || []).slice(0, 2).join(' · ')}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {(t.colors || []).slice(0, 3).map((col, i) => (
                          <div key={i} style={{
                            width: 14, height: 14,
                            backgroundColor: col,
                            border: '1px solid #e2e8f0',
                          }} />
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0', background: '#f1f5f9' }}>
                      <CardCanvas
                        cardData={sampleCard(t)}
                        tier="signature"
                        watermark={false}
                        scale={0.8}
                        side="front"
                        showStockBadge={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default TemplatesGallery;
